import React, { useMemo, memo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Grid,
  Typography,
  SmartTable,
  ActionButton,
  Search,
  DeleteDialog,
  FormDialog,
  SuccessDialog,
  CustomConfirmationDialog,
} from "../../../components";
import Counts from "./Counts";
import Fields from "./Fields";
import Status from "./Status";
import Actions from "./Actions";
import { notify } from "../../../actions/notification";
import { useDispatch, useSelector } from "react-redux";
import {
  getRecipientList,
  getRecipients,
} from "../../../actions/recipientList";
import {
  addRecipient,
  deleteRecipient,
  updateRecipient,
} from "../../../actions/recipient";
import { setParams } from "../../../actions/params";

const useStyles = makeStyles(() => ({
  root: {
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
  },
  paper: {
    height: "100%",
  },
}));

export function getColumns(headers, t) {
  // headers might be undefined until they are fetched from the API
  // let the smart table handle this case with a loader state
  if (!headers) {
    return headers;
  }

  // separate the searchable and encrypted headers
  // and show the searchable ones before he encrypted ones
  // for a cleaner representation on the UI
  return [
    ...headersToColumns({ headers, searchable: true, t }),
    ...headersToColumns({ headers, searchable: false, t }),
  ];
}

export const headersToColumns = ({ headers, searchable, t }) =>
  Object.keys(headers)
    // extract the header names
    .filter((name) => headers[name].searchable === searchable)
    // sort the headers alphabetically by name
    .sort((a, b) => a.trim() - b.trim())
    // map the column config the matching header
    .map((name) => headerToColumn({ name, searchable, t }));

export const headerToColumn = ({ name, searchable, t }) => ({
  name,
  label: name,
  getter: (recipient) => (searchable ? recipient.fields[name] : t("Encrypted")),
  sortable: false,
  testId: `recipient-${name}`,
  searchable,
});

function EditOrViewRecipientList({
  recipientList,
  onChange,
  onSearchRecipient,
  readOnly,
  isEditing,
  params,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    userId,
    loading,
    status,
    progress,
    notFound,
    errors,
    name,
    permission,
    totalCount,
    failedCount,
    emailCount,
    smsCount,
    voiceCount,
    ttyCount,
    customCount,
    recipientHeaders,
    recipients,
    _id: id,
  } = recipientList;
  const { page: recipientsPage, itemsPerPage: recipientsItemsPerPage } =
    params || {};
  const { activeDivisionId, paid } = useSelector((state) => state.storage);
  const activeCompanyId = useSelector((state) => state.user.companyId);

  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState({});
  const [recipientAddCounts, setRecipientAddCounts] = useState({
    failedCount: 0,
    insertedCount: 0,
    emailCount: 0,
    smsCount: 0,
    voiceCount: 0,
    ttyCount: 0,
    customCount: 0,
    recipientIds: [],
  });

  const RECIPIENT_ACTION = {
    ADDED: "added",
    EDITED: "edited",
    DELETED: "deleted",
    NONE: null,
  };
  const [currentRecipientAction, setCurrentRecipientAction] = useState(
    RECIPIENT_ACTION.NONE
  );

  const onEditRecipient = useCallback(
    async (recipient) => {
      if (recipient._id === undefined) {
        setCurrentRecipientAction(RECIPIENT_ACTION.ADDED);
        const addedCounts = await dispatch(
          addRecipient(
            {
              recipientListId: recipientList._id,
              recipients: [recipient.fields],
            },
            { userId, paid }
          )
        );
        setRecipientAddCounts(addedCounts);
        dispatch(getRecipients(params, id));
        dispatch(
          getRecipientList(id, {
            activeDivisionId,
            activeCompanyId,
          })
        );
      } else {
        setCurrentRecipientAction(RECIPIENT_ACTION.EDITED);
        await dispatch(
          updateRecipient(
            recipient._id,
            {
              recipientListId: recipientList._id,
              recipient: recipient.fields,
            },
            { userId }
          )
        );
        dispatch(getRecipients(params, id));
        dispatch(
          getRecipientList(id, {
            activeDivisionId,
            activeCompanyId,
          })
        );
      }
    },
    [dispatch, recipientList]
  );

  const onDeleteRecipient = useCallback(
    (recipient) => {
      dispatch(
        deleteRecipient(recipient._id, { userId, activeDivisionId })
      ).then(() => {
        dispatch(getRecipients(params, id));
        dispatch(
          getRecipientList(id, {
            activeDivisionId,
            activeCompanyId,
          })
        );
      });
    },
    [dispatch, recipientList]
  );

  const clearRecipientAddCountsState = () => {
    setRecipientAddCounts({
      failedCount: 0,
      insertedCount: 0,
      emailCount: 0,
      smsCount: 0,
      voiceCount: 0,
      ttyCount: 0,
      customCount: 0,
      recipientIds: [],
    });
  };

  const onEdit = useCallback(
    (recipient) => {
      setSelectedRecipient(recipient);
      setOpen(!open);
    },
    [setOpen, setSelectedRecipient]
  );

  const validateInputs = (result) => {
    const nameRegex = /^[\sa-zA-Z0-9_-]{0,100}$/g;
    const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g;
    const smsRegex = /^[0-9]{0,10}$/g;
    const { contactType, contact, name } = result;
    if (!nameRegex.test(name)) {
      return { valid: false, error: "name" };
    }
    switch (contactType) {
      case "email":
        return { valid: emailRegex.test(contact), error: "contact type" };
      case "voice":
      case "tty":
      case "sms":
        return { valid: smsRegex.test(contact), error: "contact type" };
      default:
        return { valid: false, error: "" };
    }
  };

  const onConfirmEdit = async (result) => {
    const { valid, error } = await validateInputs(result);
    if (!valid) {
      dispatch(
        notify({
          type: "error",
          message: `Invalid value for ${error} ${
            error !== "name" ? result.contactType : ""
          }!`,
        })
      );
      return false;
    }
    setOpen(false);
    selectedRecipient.fields = result;
    await onEditRecipient(selectedRecipient);
    setCurrentRecipientAction(
      selectedRecipient?._id ? RECIPIENT_ACTION.EDITED : RECIPIENT_ACTION.ADDED
    );
    setSuccessOpen(true);
  };
  const onDelete = useCallback((recipient) => {
    setSelectedRecipient(recipient);
    setDeleteOpen(true);
  });
  const onConfirmDelete = useCallback(() => {
    setDeleteOpen(false);
    onDeleteRecipient(selectedRecipient);
    setCurrentRecipientAction(RECIPIENT_ACTION.DELETED);
    setSuccessOpen(true);
  });
  const columns = useMemo(() => {
    const columns = getColumns(recipientHeaders, t);

    if (isEditing && columns) {
      columns.push({
        name: "actions",
        getter: (recipient) => (
          <Actions
            recipient={recipient}
            onEdit={() => onEdit(recipient)}
            onDelete={() => onDelete(recipient)}
          />
        ),
        label: t("Actions"),
        sortable: false,
        align: "center",
      });
    }
    return columns;
  }, [recipientHeaders, t]);
  const formFields = useMemo(() => {
    if (recipientHeaders && selectedRecipient._id) {
      delete recipientHeaders["ssn"];
    }
    return {
      ...recipientHeaders,
      contactType: {
        type: "array",
        items: {
          type: "string",
          enum: ["sms", "voice", "email", "tty"],
        },
      },
      // Earlier contactType was not allowed to be edited
      // contactType: !selectedRecipient._id
      //   ? {
      //       type: "array",
      //       items: {
      //         type: "string",
      //         enum: ["sms", "voice", "email", "tty"],
      //       },
      //     }
      //   : {},
    };
  }, [recipientHeaders, selectedRecipient]);

  if (notFound) {
    return (
      <Typography variant="h6">{t("Recipient list not found")}</Typography>
    );
  }

  return (
    <Box p={2} className={classes.root}>
      <Grid container spacing={2} justify="stretch" alignItems="stretch">
        <Grid item sm={12} md={6}>
          <Fields
            name={name}
            permission={permission}
            errors={errors}
            readOnly={readOnly}
            onChange={onChange}
            className={classes.paper}
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <Counts
            failedCount={failedCount}
            totalCount={totalCount}
            smsCount={smsCount}
            emailCount={emailCount}
            voiceCount={voiceCount}
            ttyCount={ttyCount}
            customCount={customCount}
            loading={loading}
            className={classes.paper}
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <Status
            loading={loading}
            status={status}
            progress={progress}
            name={name}
            className={classes.paper}
          />
        </Grid>
        <FormDialog
          open={open}
          resource="Recipient"
          onClose={() => setOpen(false)}
          onSave={onConfirmEdit}
          fields={formFields}
          data={selectedRecipient.fields}
        />
        <DeleteDialog
          open={Boolean(deleteOpen)}
          onClose={() => setDeleteOpen(false)}
          onDelete={onConfirmDelete}
          name={selectedRecipient?.fields?.name}
          resource={"Recipient"}
        />
        {currentRecipientAction === RECIPIENT_ACTION.ADDED ? (
          <CustomConfirmationDialog
            open={Boolean(successOpen)}
            onClose={() => {
              setSuccessOpen(false);
              clearRecipientAddCountsState();
              setCurrentRecipientAction(RECIPIENT_ACTION.NONE);
            }}
            dialogText={`Number of successfully added
            recipients: ${recipientAddCounts?.insertedCount}.
            Number of recipients failed to be added: ${recipientAddCounts?.failedCount}`}
          />
        ) : (
          <SuccessDialog
            open={Boolean(successOpen)}
            onClose={() => {
              setSuccessOpen(false);
              setCurrentRecipientAction(RECIPIENT_ACTION.NONE);
              setSelectedRecipient({});
            }}
            name={selectedRecipient?.fields?.name}
            action={currentRecipientAction}
            resource={"Recipient"}
          />
        )}
        {isEditing && (
          <Grid item xs={12} sm={6}>
            <Grid container spacing={2}>
              <ActionButton
                onClick={() => {
                  setOpen(!open);
                  setSelectedRecipient({ fields: {} });
                }}
                text={t("Add Recipient")}
                data-test-id="add-recipient-button"
              />
              <Grid item xs={12} sm={6} md>
                <Search
                  variant="outlined"
                  size="small"
                  label={t("Search")}
                  onSearch={onSearchRecipient}
                  defaultValue={""}
                  debounce={300}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid item xs={12} xl={12}>
          <RecipientsDataTable
            recipients={recipients}
            recipientHeaders={recipientHeaders}
            notFoundMessage={t("No recipients found")}
            page={recipientsPage}
            itemsPerPage={recipientsItemsPerPage}
            isSaved={Boolean(id)}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default memo(EditOrViewRecipientList);

export const RecipientsDataTable = ({
  isCampaignsPage,
  recipients,
  recipientHeaders,
  isEditing,
  page = 0,
  itemsPerPage,
  isSaved = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const getColumns = (headers, t) => {
    // headers might be undefine until they are fethced from the API
    // let the smart table handle this case with a loader state
    if (!headers) {
      return headers;
    }

    // separate the searchable and encrypted headers
    // and show the searchable ones before he encyrpted ones
    // for a cleaner representation on the UI
    return [
      ...headersToColumns({ headers, searchable: true, t }),
      ...headersToColumns({ headers, searchable: false, t }),
    ];
  };

  const columns = useMemo(() => {
    const columns = getColumns(recipientHeaders, t);

    if (isEditing && columns) {
      columns.push({
        name: "actions",
        getter: (recipient) => (
          <Actions
            recipient={recipient}
            onEdit={() => onEdit(recipient)}
            onDelete={() => onDelete(recipient)}
          />
        ),
        label: t("Actions"),
        sortable: false,
        align: "center",
      });
    }
    return columns;
  }, [recipientHeaders, t]);

  const onRecipientsPageChange = useCallback(
    (ev, page) => {
      const params = !isCampaignsPage ? { page } : { listPage: page };
      dispatch(setParams(params));
    },
    [dispatch, isCampaignsPage]
  );

  const onRecipientsItemsPerPageChange = useCallback(
    (ev) => {
      const params = !isCampaignsPage
        ? { itemsPerPage: ev.target.value, page: 0 } // Reset page to 0 when changing items per page
        : { itemsPerPage: ev.target.value, listPage: 0 };
      dispatch(setParams(params));
    },
    [dispatch, isCampaignsPage]
  );

  return (
    <SmartTable
      columns={columns}
      data={recipients?.data || []}
      totalCount={recipients?.totalCount || 0}
      notFoundMessage={t("No recipients found")}
      page={page}
      hidePagination={!isSaved}
      itemsPerPage={itemsPerPage}
      onPageChange={onRecipientsPageChange}
      onItemsPerPageChange={onRecipientsItemsPerPageChange}
      testId="recipient"
    />
  );
};
